import { UUID } from "src/types/shared.type";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ModifierCategory } from "src/interfaces/modifier.interface";
import { NOT_FOUND_MODIFIER_CATEGORY_INDEX } from "src/constants/constants";

interface ModifierState {
  modifierCategories: ModifierCategory[];
  filteredModifierCategories: ModifierCategory[];
  toBeDeletedModifierCategoryId: UUID | null;
}

const initialState: ModifierState = {
  modifierCategories: [],
  filteredModifierCategories: [],
  toBeDeletedModifierCategoryId: null,
};

export const modifierCategoriesSlice = createSlice({
  name: "modifierCategories",
  initialState,
  reducers: {
    set_modifier_categories: (state, action: PayloadAction<ModifierCategory[]>) => {
      state.modifierCategories = action.payload;
      state.filteredModifierCategories = action.payload;
    },
    
    add_modifier_category: (state, action: PayloadAction<ModifierCategory>) => {
      state.modifierCategories.push(action.payload);
      state.filteredModifierCategories.push(action.payload);
    },
    
    update_modifier_category: (state, action: PayloadAction<Partial<ModifierCategory> & { correlationId: UUID }>) => {
      const modifierCategoryIndex = state.modifierCategories.findIndex(
        modifierCategory => modifierCategory.correlationId === action.payload.correlationId
      );
      if (modifierCategoryIndex !== NOT_FOUND_MODIFIER_CATEGORY_INDEX) {
        state.modifierCategories[modifierCategoryIndex] = {
          ...state.modifierCategories[modifierCategoryIndex],
          ...action.payload,
        };
      }

      const filteredCategoryIndex = state.filteredModifierCategories.findIndex(
        modifierCategory => modifierCategory.correlationId === action.payload.correlationId
      );
      if (filteredCategoryIndex !== NOT_FOUND_MODIFIER_CATEGORY_INDEX) {
        state.filteredModifierCategories[filteredCategoryIndex] = {
          ...state.filteredModifierCategories[filteredCategoryIndex],
          ...action.payload,
        };
      }
    },
    
    remove_modifier_category: (state, action: PayloadAction<UUID>) => {
      state.modifierCategories = state.modifierCategories.filter(
        modifierCategory => modifierCategory.correlationId !== action.payload
      );
      state.filteredModifierCategories = state.filteredModifierCategories.filter(
        modifierCategory => modifierCategory.correlationId !== action.payload
      );
    },
    
    // Search & Filter
    search_modifier_categories: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload.toLowerCase();
      state.filteredModifierCategories = state.modifierCategories.filter(
        modifierCategory => modifierCategory.name?.toLowerCase().includes(searchTerm) ?? false
      );
    },
    
    sort_modifier_categories: (state, action: PayloadAction<'aToZ' | 'zToA'>) => {
      const sortedModifierCategories = [...state.filteredModifierCategories].sort((modifierCategoryA, modifierCategoryB) => {
        const modifierCategoryNameA = modifierCategoryA.name?.toLowerCase() ?? '';
        const modifierCategoryNameB = modifierCategoryB.name?.toLowerCase() ?? '';
        
        if (action.payload === 'aToZ') {
          return modifierCategoryNameA.localeCompare(modifierCategoryNameB);
        } else {
          return modifierCategoryNameB.localeCompare(modifierCategoryNameA);
        }
      });
      
      state.filteredModifierCategories = sortedModifierCategories;
    },
    
    set_delete_modal_category_id: (state, action: PayloadAction<UUID | null>) => {
      state.toBeDeletedModifierCategoryId = action.payload;
    }
  },
});

export const {
  set_modifier_categories,
  add_modifier_category,
  update_modifier_category,
  remove_modifier_category,
  search_modifier_categories,
  sort_modifier_categories,
  set_delete_modal_category_id,
} = modifierCategoriesSlice.actions;

export default modifierCategoriesSlice.reducer;
